/* eslint-disable @typescript-eslint/no-namespace */
import { UseTrixEditorProps, useTrixEditor } from 'hooks/trix/use_trix_editor'
import { useMediaQuery } from 'hooks/use_media_query'
import React, { useContext, useRef } from 'react'

import { MEDIA_QUERIES } from 'design'
import { SROnly } from 'primitives/accessibility/sr_only'

import { DevicesContext } from '../../contexts/devices_context'
import { DesktopTrixForm, MobileTrixForm } from './responsive_forms'
import { SCREENREADER_ID, TrixEditor } from './trix_editor'

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'trix-toolbar': any
    }
  }
}

export const TRIX_TOOLBAR_MINIMUM_WIDTH = 230
export const TRIX_TOOLBAR_PADDING = 32

export type TrixFormProps = {
  id: string
  trixHtml?: any
  onChange?: (value: any) => void
  onSubmit: (html: any) => Promise<any>
  onCancel?: (event: any) => void
  hasAudioDevices?: boolean
  hasVideoDevices?: boolean
  passUpFocusAtStart?: (f: () => void) => void
  onActiveTypeChanged?: () => void
  activeType?: 'message' | 'note'
  placeholder?: string
  variant?: 'default' | 'split'
  isEditing?: boolean
}

export type ResponsiveTrixFormProps = React.PropsWithChildren<
  Omit<UseTrixEditorProps, 'handleEditorReady' | 'handleChange'> &
    Pick<
      TrixFormProps,
      | 'id'
      | 'isEditing'
      | 'passUpFocusAtStart'
      | 'onActiveTypeChanged'
      | 'activeType'
      | 'onCancel'
      | 'variant'
      | 'hasAudioDevices'
      | 'hasVideoDevices'
    >
>

export const TrixForm = ({
  id,
  isEditing = false,
  onChange,
  onSubmit,
  trixHtml,
  variant,
  onActiveTypeChanged,
  onCancel,
  passUpFocusAtStart,
  activeType,
  placeholder,
  ...props
}: TrixFormProps) => {
  const formRef = useRef<HTMLFormElement>(null)

  const { handleEditorReady, handleChange, ...useTrixEditorProps } =
    useTrixEditor({ id: id || '', onChange, onSubmit, trixHtml })

  const { hasAudioDevices, hasVideoDevices } = useContext(DevicesContext)

  return (
    <TrixContainer
      ref={formRef}
      id={id}
      variant={variant}
      activeType={activeType}
      onActiveTypeChanged={onActiveTypeChanged}
      onCancel={onCancel}
      passUpFocusAtStart={passUpFocusAtStart}
      isEditing={isEditing}
      {...useTrixEditorProps}
      hasAudioDevices={hasAudioDevices}
      hasVideoDevices={hasVideoDevices}
    >
      <TrixEditor
        id={id}
        onChange={handleChange}
        onEditorReady={handleEditorReady}
        placeholder={placeholder}
        {...props}
      />
      <SROnly id={SCREENREADER_ID}>
        Type a message, attach files and media
      </SROnly>
    </TrixContainer>
  )
}

const TrixContainer = React.forwardRef<
  HTMLFormElement,
  React.PropsWithChildren<ResponsiveTrixFormProps>
>(({ children, ...props }, ref) => {
  const desktop = useMediaQuery(MEDIA_QUERIES.md)

  return desktop ? (
    <DesktopTrixForm ref={ref} {...props}>
      {children}
    </DesktopTrixForm>
  ) : (
    <MobileTrixForm ref={ref} {...props}>
      {children}
    </MobileTrixForm>
  )
})
TrixContainer.displayName = 'TrixContainer'
